import { BillingCycle } from '@/firebase/auth/auth-jwt-schema'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { prepareRtkQueryHeaders, transformRtkQueryResponse } from '@/store/apis/rtk-query-utils'
import { kPaymentsValidatePromoCodeApiPath, kPaymentsCheckoutPricesApiPath, kPaymentsTrialDaysApiPath } from '@/constants/constants-api-paths'
import { PromoCodeResponseSchema, PromoCodeResponse, Promotion } from '@/checkout/schemas/promo-code.schema'
import { CheckoutPricesSchema, CheckoutPrices } from '@/checkout/schemas/price.schema'
import { TrialDaysResponse, TrialDaysResponseSchema } from '@/checkout/schemas/trial.schema'

interface checkoutState {
  billingCycle: BillingCycle
  promotion: Promotion | null
  annualPlanMonthlyPrice: number
  monthlyPlanMonthlyPrice: number
  displayTrialDays: number
  actualTrialDays: number
}

const initialState: checkoutState = {
  billingCycle: BillingCycle.ANNUAL,
  promotion: null,
  annualPlanMonthlyPrice: 0,
  monthlyPlanMonthlyPrice: 0,
  displayTrialDays: 7,
  actualTrialDays: 8,
}

const checkoutSlice = createSlice({
  name: 'checkoutState',
  initialState,
  reducers: {
    nullifyData: () => initialState,
    setBillingCycle: (state, action: PayloadAction<BillingCycle>) => {
      state.billingCycle = action.payload
    },
    setPromoCodeResponse: (state, action: PayloadAction<Promotion | null>) => {
      state.promotion = action.payload
    },
    setCheckoutPrices: (state, action: PayloadAction<CheckoutPrices>) => {
      state.annualPlanMonthlyPrice = action.payload.standard_annual_price.unit_amount / 12
      state.monthlyPlanMonthlyPrice = action.payload.standard_monthly_price.unit_amount
    },
    setTrialDays: (state, action: PayloadAction<TrialDaysResponse>) => {
      state.displayTrialDays = action.payload.display_trial_days
      state.actualTrialDays = action.payload.actual_trial_days
    },
  },
})

// Add RTK Query API
export const checkoutApi = createApi({
  reducerPath: 'checkoutApi',
  baseQuery: fetchBaseQuery({
    baseUrl: kPaxtonAppApiRESTBaseUrl(),
    prepareHeaders: async (headers) => await prepareRtkQueryHeaders(headers),
  }),
  endpoints: (builder) => ({
    validatePromoCode: builder.query<PromoCodeResponse, string>({
      query: (promoCode) => ({
        url: `${kPaymentsValidatePromoCodeApiPath}`,
        method: 'GET',
        params: {
          promo_code: promoCode,
        },
      }),
      keepUnusedDataFor: 0,
      transformResponse: (response) => {
        const validatedResponse = PromoCodeResponseSchema.safeParse(response)
        if (!validatedResponse.success) {
          throw new Error(validatedResponse.error.message)
        }
        return validatedResponse.data as PromoCodeResponse
      },

      // Capture errors to Sentry
      transformErrorResponse: async (baseQueryReturnValue, meta, arg) => await transformRtkQueryResponse(baseQueryReturnValue, meta, arg),

      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          if (data && data.success && data.promo_code) {
            dispatch(setPromoCodeResponse(data.promo_code))
          }
        } catch (error) {
          console.log('Error validating promo code', error)
        }
      },
    }),
    fetchCheckoutPrices: builder.query<CheckoutPrices, void>({
      query: () => ({
        url: `${kPaymentsCheckoutPricesApiPath}`,
        method: 'GET',
      }),
      keepUnusedDataFor: 60,
      transformResponse: (response) => {
        const validatedResponse = CheckoutPricesSchema.safeParse(response)
        if (!validatedResponse.success) {
          throw new Error(validatedResponse.error.message)
        }
        return validatedResponse.data as CheckoutPrices
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          if (data) {
            dispatch(setCheckoutPrices(data))
          }
        } catch (error) {
          console.log('Error fetching checkout prices', error)
        }
      },
      // Capture errors to Sentry
      transformErrorResponse: async (baseQueryReturnValue, meta, arg) => await transformRtkQueryResponse(baseQueryReturnValue, meta, arg),
    }),
    fetchTrialDays: builder.query<TrialDaysResponse, void>({
      query: () => ({
        url: `${kPaymentsTrialDaysApiPath}`,
        method: 'GET',
      }),
      keepUnusedDataFor: 60,
      transformResponse: (response) => {
        const validatedResponse = TrialDaysResponseSchema.safeParse(response)
        if (!validatedResponse.success) {
          throw new Error(validatedResponse.error.message)
        }
        return validatedResponse.data as TrialDaysResponse
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          dispatch(setTrialDays(data))
        } catch (error) {
          console.log('Error fetching trial days', error)
        }
      },
    }),
  }),
})

export const { useValidatePromoCodeQuery, useFetchCheckoutPricesQuery, useFetchTrialDaysQuery } = checkoutApi

export const { nullifyData, setBillingCycle, setPromoCodeResponse, setCheckoutPrices, setTrialDays } = checkoutSlice.actions

export default checkoutSlice.reducer
